import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
// import logo from '../../assets/logo.png';

interface MessageDisplayProps {
  title: string;
  message: string;
  subtitle?: string;
  showDashboardLink?: boolean;
  dashboardText?: string;
  dashboardPath?: string;
  supportEmail?: string;
  customActions?: React.ReactNode;
  variant?: 'error' | 'success' | 'info' | 'warning';
}

const MessageDisplay: React.FC<MessageDisplayProps> = ({
  title,
  message,
  subtitle,
  showDashboardLink = false,
  dashboardText = "Go to dashboard",
  dashboardPath = "/dashboard",
  supportEmail = "support@infollion.com",
  customActions,
  variant = 'error'
}) => {
  const navigate = useNavigate();

  const handleNavigateToDashboard = () => {
    navigate(dashboardPath);
  };

  // Color scheme based on variant
  const getColorScheme = () => {
    switch (variant) {
      case 'success':
        return {
          primary: '#4caf50',
          secondary: '#2e7d32'
        };
      case 'info':
        return {
          primary: '#2196f3',
          secondary: '#1976d2'
        };
      case 'warning':
        return {
          primary: '#ff9800',
          secondary: '#f57c00'
        };
      case 'error':
      default:
        return {
          primary: 'rgb(236, 147, 36)',
          secondary: '#d32f2f'
        };
    }
  };

  const colors = getColorScheme();

  // Helper function to render message with potential email link
  const renderMessageWithEmailLink = (text: string, email: string) => {
    const emailRegex = new RegExp(`(${email})`, 'g');
    const parts = text.split(emailRegex);
    
    return parts.map((part, index) => {
      if (part === email) {
        return (
          <a
            key={index}
            href={`mailto:${email}`}
            style={{
              color: 'rgb(0, 0, 238)',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            {email}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh',
        px: 2,
      }}
    >

      {/* Content Container */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: { xs: '100%', sm: '80%', md: '60%' },
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            color: colors.primary,
            fontWeight: 'bold',
            marginBottom: 2,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>

        {/* Subtitle (if provided) */}
        {subtitle && (
          <Typography
            variant="h6"
            sx={{
              color: colors.secondary,
              fontWeight: 500,
              marginBottom: 3,
              textAlign: 'center',
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Main Message */}
        <Typography
          variant="body1"
          sx={{
            marginBottom: 3,
            textAlign: 'center',
            px: { xs: 2, sm: 4, md: 6 },
            lineHeight: 1.6,
          }}
        >
          {renderMessageWithEmailLink(message, supportEmail)}
        </Typography>

        {/* Actions Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mt: 2,
          }}
        >
          {/* Dashboard Link */}
          {showDashboardLink && (
            <Button
              onClick={handleNavigateToDashboard}
              variant="outlined"
              sx={{
                color: colors.primary,
                borderColor: colors.primary,
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: colors.secondary,
                  backgroundColor: `${colors.primary}10`,
                },
              }}
            >
              {dashboardText}
            </Button>
          )}

          {/* Custom Actions */}
          {customActions && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {customActions}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MessageDisplay;